/**
 * The MO OS surface at `/mo`.
 *
 * A separate route from upstream's `Home`, which the fork's upstream author is
 * actively developing. Keeping them separate is what bounds merge friction, per
 * the A1 design.
 *
 * Structure follows the actual question the owner has: what do I owe, what am I
 * waiting on, what needs a judgement from me. The briefing's stated limitations are
 * rendered rather than hidden, because a surface that implies it knows your calendar
 * when it does not is worse than one that says so.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  dueLabel,
  fetchAttention,
  fetchBriefing,
  groupFeed,
  personFromRef,
  resolveAttention,
  type AttentionItem,
  type Briefing,
  type Loaded,
} from './api.js';
import { PersonDossier } from './PersonDossier.js';
import { Panel } from './Panel.js';
import { Aurebesh } from './Aurebesh.js';
import { Icon } from './Icon.js';
import './commandDeck.css';

export function MoHome(): JSX.Element {
  const [feed, setFeed] = useState<Loaded<{ items: AttentionItem[] }>>({ state: 'loading' });
  const [brief, setBrief] = useState<Loaded<Briefing>>({ state: 'loading' });
  const [person, setPerson] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [f, b] = await Promise.all([fetchAttention(), fetchBriefing()]);
    setFeed(f);
    setBrief(b);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onResolve = useCallback(
    async (id: number) => {
      await resolveAttention(id);
      await load();
    },
    [load],
  );

  if (feed.state === 'loading') return <main className="mo-home">Loading.</main>;

  if (feed.state === 'disabled') {
    return (
      <main className="mo-home">
        <h1>MO OS</h1>
        <p>{feed.message}</p>
      </main>
    );
  }

  if (feed.state === 'error') {
    return (
      <main className="mo-home">
        <h1>MO OS</h1>
        <p>Could not load the feed: {feed.message}</p>
      </main>
    );
  }

  if (person !== null) {
    return <PersonDossier name={person} onBack={() => setPerson(null)} />;
  }

  const grouped = groupFeed(feed.value.items);
  /* See the two panels below on why the judgement queue is split by kind. */
  const identity = grouped.decide.filter((i) => i.kind === 'unresolved-identity');
  const uncertain = grouped.decide.filter((i) => i.kind === 'low-confidence');

  return (
    <main className="mo-home">
      {/*
        THE ARCHIVES, REDESIGNED 2026-08-28.

        "Knowlege Page is Still not done at all, this need to be redesign." He is right and it was
        the oldest surface in the app: bare `h1`, `h2`, `ul` and an unstyled `Done` button, written
        before the deck existed and never revisited while five passes went into the deck itself.

        It is now built on the SHARED `Panel` primitive rather than restyled, which is exactly what
        that file was extracted for: "the primitives move here and both halves import them". The
        alternative was a third set of classes that would agree with the deck today and drift from
        it by the next change. Nothing about what this page knows or does has changed; only what it
        is made of.
      */}
      <header className="mo-home-head">
        <p className="mo-home-eyebrow">
          <Icon name="notes" size={14} />
          The archives
          <Aurebesh seed="archives-head" length={6} size={11} />
        </p>
        <h1 className="mo-home-title">What needs you</h1>
      </header>

      <div className="mo-home-stack">
        {grouped.health.length > 0 && (
          <Panel
            icon="agent"
            hue="health"
            name="Ingestion"
            caption="Where the intake needs a hand"
            headline={`${grouped.health.length} ${grouped.health.length === 1 ? 'check' : 'checks'}`}
            sub="needs attention"
            subTone="warn"
            defaultOpen
          >
            <ItemList items={grouped.health} onResolve={onResolve} onOpenPerson={setPerson} />
          </Panel>
        )}

        <Panel
          icon="owed"
          hue="action"
          name="What you owe"
          caption="Your promises, still open"
          headline={String(grouped.owe.length)}
          sub={grouped.owe.length === 1 ? 'commitment' : 'commitments'}
          defaultOpen
        >
          <ItemList items={grouped.owe} onResolve={onResolve} onOpenPerson={setPerson} />
        </Panel>

        <Panel
          icon="waiting"
          hue="waiting"
          name="Waiting on others"
          caption="Owed to you"
          headline={String(grouped.waiting.length)}
          sub={grouped.waiting.length === 1 ? 'item' : 'items'}
          defaultOpen
        >
          <ItemList items={grouped.waiting} onResolve={onResolve} onOpenPerson={setPerson} />
        </Panel>

        {/*
          TWO PANELS, NOT ONE. `groupFeed` puts `unresolved-identity` and `low-confidence` in the
          same bucket, and on his vault that is thirty-four rows of two completely different
          questions interleaved: "which of these four people is this address" and "is this
          sentence actually a commitment". They take different answers and different amounts of
          thought, so reading them as one queue is most of why it felt endless.

          Split here rather than in `groupFeed`, which other callers and its own tests depend on.
        */}
        <Panel
          icon="judgement"
          hue="ask"
          name="Who is this?"
          caption="One name, more than one person"
          headline={String(identity.length)}
          sub={identity.length === 1 ? 'question' : 'questions'}
          defaultOpen
        >
          <ItemList items={identity} onResolve={onResolve} onOpenPerson={setPerson} />
        </Panel>

        <Panel
          icon="update"
          hue="waiting"
          name="Uncertain readings"
          caption="It is not sure it read these right"
          headline={String(uncertain.length)}
          sub={uncertain.length === 1 ? 'to confirm' : 'to confirm'}
          defaultOpen
        >
          <ItemList items={uncertain} onResolve={onResolve} onOpenPerson={setPerson} />
        </Panel>

        {brief.state === 'ready' && <BriefingPanel brief={brief.value} onOpenPerson={setPerson} />}
      </div>
    </main>
  );
}

/**
 * How many rows a panel shows before it stops.
 *
 * "Needs a decision goes forever." It did: thirty-four near-identical rows in one panel, which
 * made that panel taller than the other four put together and turned a two-column layout back
 * into one long scroll. A queue you cannot see the end of is not a queue, it is a wall.
 *
 * Eight is what fits beside its neighbours without the grid going ragged, and the remainder is
 * STATED and one press away. Never a silent cap: a panel showing eight of thirty-four with no
 * hint of the other twenty-six reads as "you have eight questions", which is the quiet kind of
 * wrong this whole surface exists to avoid.
 */
const SHOWN = 8;

function ItemList({
  items,
  onResolve,
  onOpenPerson,
}: {
  items: AttentionItem[];
  onResolve: (id: number) => void;
  onOpenPerson: (name: string) => void;
}): JSX.Element {
  const [all, setAll] = useState(false);
  if (items.length === 0) return <p className="mo-empty">Nothing here.</p>;

  const shown = all ? items : items.slice(0, SHOWN);
  const hidden = items.length - shown.length;

  return (
    <>
    <ul className="mo-list">
      {shown.map((i) => {
        const who = personFromRef(i.ref);
        return (
          /* `mo-row` picks up the deck's body layer: hue edge, cut corner, hover. */
          <li key={i.id} className="mo-row">
            <span className="mo-row-body">
              <span className="mo-item-title">{i.title}</span>
              {who !== '' && (
                <button type="button" className="mo-link" onClick={() => onOpenPerson(who)}>
                  {who}
                </button>
              )}
            </span>
            {/* The same control the deck's signal cards use, rather than a browser default
                button. It was the single most obviously unfinished thing on this page. */}
            <button type="button" className="cdeck-signal-btn" onClick={() => onResolve(i.id)}>
              <Icon name="action" size={13} />
              Done
            </button>
          </li>
        );
      })}
    </ul>
    {hidden > 0 && (
      <button type="button" className="mo-more" onClick={() => setAll(true)}>
        {hidden} more
        <Icon name="chevron" size={12} />
      </button>
    )}
    {all && items.length > SHOWN && (
      <button type="button" className="mo-more" onClick={() => setAll(false)}>
        Show fewer
      </button>
    )}
    </>
  );
}

function BriefingPanel({
  brief,
  onOpenPerson,
}: {
  brief: Briefing;
  onOpenPerson: (name: string) => void;
}): JSX.Element {
  return (
    <>
      {brief.overdue.length > 0 && (
        <Panel
          icon="clock"
          hue="action"
          name="Overdue"
          caption="Past the date they were agreed for"
          headline={String(brief.overdue.length)}
          sub={brief.overdue.length === 1 ? 'commitment' : 'commitments'}
          subTone="crit"
          defaultOpen
        >
          <ul className="mo-list">
            {brief.overdue.map((c) => (
              <li key={c.path} className="mo-row">
                <span className="mo-row-body">
                  <span className="mo-item-title">{c.text}</span>
                </span>
                <span className="mo-due">{dueLabel(c.due)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel
        icon="waiting"
        hue="know"
        name="By person"
        caption="Who the open work sits with"
        headline={String(brief.counterparties.length)}
        sub={brief.counterparties.length === 1 ? 'person' : 'people'}
      >
        {brief.counterparties.length === 0 ? (
          <p className="mo-empty">Nothing outstanding.</p>
        ) : (
          <ul className="mo-list">
            {brief.counterparties.map((c) => (
              <li key={c.name} className="mo-row">
                <span className="mo-row-body">
                  <button type="button" className="mo-link" onClick={() => onOpenPerson(c.name)}>
                    {c.name}
                  </button>
                </span>
                <span className="mo-counts">
                  you owe {c.owedByOwner.length}, waiting on {c.owedToOwner.length}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/*
        STAYS, and stays last. The file's own opening comment is the argument: a surface that
        implies it knows your calendar when it does not is worse than one that says so. Putting it
        in a panel does not soften it; it means the admission is built the same way as everything
        it qualifies, instead of looking like a footnote someone forgot to style.
      */}
      <Panel
        icon="judgement"
        hue="health"
        name="What this does not know"
        caption="Stated, not hidden"
        headline={String(brief.limitations.length)}
        sub={brief.limitations.length === 1 ? 'gap' : 'gaps'}
      >
        <ul className="mo-list mo-limitations">
          {brief.limitations.map((l) => (
            <li key={l} className="mo-row">
              <span className="mo-row-body">{l}</span>
            </li>
          ))}
        </ul>
      </Panel>
    </>
  );
}
